import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import ContactLines from '../../components/ContactLines';
import SuppressionBadges from '../../components/SuppressionBadges';
import RetentionTrendPanel from './RetentionTrendPanel';
import GuestEventRoster from './GuestEventRoster';

// Club Dashboard. 10/23/26: rebuilt as a totals page (see the block above the default export): one card per area the
// club owns plus the free-membership cap, with admin-set goals. The Sprint 21.1 panels (In Club Today, Trials Needing
// Help, Trial Goal) were taken off the page until retention is defined and they can be shown as numbers; they are in
// git history and the backend (getClubDashboardSummary) still computes them. The referral tools, Guests Today and the
// wrong-club list below are unchanged. Failed Payments stays in Retention (removed from here 9/1/26).

// Membership bought at one club but played mostly at another (BK, 10/7/26). The club where they PLAY owns
// the outreach: move them to the actual local rate (e.g. $119 at Denton -> $139 here); 10% off only if it is
// needed to save a cancellation. The 60% line is tied to HQ's quarterly revenue adjustment.
const CLUB_NAMES = { DEN: 'Denton', LEW: 'Lewisville', MCK: 'McKinney', GB: 'Green Bay', MF: 'Menomonee Falls', WA: 'West Allis' };
const SWITCH_OUTCOMES = [
  'No answer', 'Left voicemail', 'Spoke with — will switch',
  'Switched to current rate', 'Upgraded to Nationwide', 'Saved — 10% off', 'Waived by manager', 'Declined — cancelling',
];

export function SwitchTask({ t, clubId, repName, onLogged }) {
  const { post } = useApi();
  const [open, setOpen] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const first = (t.name || '').split(' ')[0] || 'there';
  const gap = t.currentPrice != null && t.localPrice != null ? t.localPrice - t.currentPrice : null;
  const isLocalPlan = !/nationwide/i.test(t.planName || '');
  const script = isLocalPlan
    ? `Hi ${first}, it's ${repName || 'a member of the team'} at The Picklr ${CLUB_NAMES[t.playClub] || t.playClub}. We love having you here — I noticed you're playing with us most of the time, but your ${t.planName || 'local'} membership is only good at ${CLUB_NAMES[t.memberClub] || t.memberClub}. A Nationwide membership${t.localPrice != null ? ` ($${t.localPrice}/mo)` : ''} would cover you at every club, including here. Would you like me to set that up for you?`
    : `Hi ${first}, it's ${repName || 'a member of the team'} at The Picklr ${CLUB_NAMES[t.playClub] || t.playClub}. We love having you here — you've been playing with us most of the time, and your membership was set up at our ${CLUB_NAMES[t.memberClub] || t.memberClub} club. Nationwide memberships are priced by the club where you play, so we'd like to move yours over to our current Nationwide rate${t.localPrice != null ? ` of $${t.localPrice}/mo` : ''}. Can I take care of that for you today?`;

  async function save() {
    if (!outcome) { setErr('Pick an outcome'); return; }
    setSaving(true); setErr(null);
    try {
      await post('/club-dashboard/membership-switch/outcome', { email: t.email, memberId: t.memberId, locationId: clubId, outcome, notes });
      setOpen(false); setOutcome(''); setNotes('');
      onLogged();
    } catch (e) { setErr(e.message || 'Could not save'); } finally { setSaving(false); }
  }

  return (
    <div style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{t.name || t.email}</div>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: isLocalPlan ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)', color: isLocalPlan ? '#60a5fa' : '#f59e0b' }}>
          {isLocalPlan ? 'NATIONWIDE UPSELL' : 'RATE FIX'}
        </span>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {isLocalPlan
            ? <>{CLUB_NAMES[t.memberClub] || t.memberClub} plan · {t.visitsAtPlayClub} visit{t.visitsAtPlayClub === 1 ? '' : 's'} here in the last 30 days</>
            : <>bought at {CLUB_NAMES[t.memberClub] || t.memberClub} · {Math.round(t.playPct)}% of last 90 days here ({t.visitsAtPlayClub} of {t.visitsTotal} visits
          {t.visitsAtMemberClub === 0 ? ', never at ' + (CLUB_NAMES[t.memberClub] || t.memberClub) : ''})</>}
        </div>
      </div>
      <ContactLines phone={t.phone} email={t.email} name={t.name} clubId={clubId} compact />
      <SuppressionBadges doNotText={t.doNotText} doNotEmail={t.doNotEmail} />
      <div style={{ fontSize: 12, marginTop: 4 }}>
        {isLocalPlan ? <>{t.planName} — good at {CLUB_NAMES[t.memberClub] || t.memberClub} only</>
          : (t.currentPrice != null ? <>Pays about <strong>${t.currentPrice}</strong></> : 'Current price not on file')}
        {t.localPrice != null && <> · {CLUB_NAMES[t.playClub] || 'this club'}'s Nationwide rate <strong>${t.localPrice}</strong>{gap > 0 && <span style={{ color: '#f59e0b' }}> (+${gap}/mo)</span>}</>}
        {t.lastOutcome && <span style={{ color: 'var(--text-muted)' }}> · last: {t.lastOutcome}{t.lastContactBy ? ` (${t.lastContactBy})` : ''}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setShowScript(v => !v)}>{showScript ? 'Hide script' : 'Script (draft)'}</button>
        <button className="btn" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setOpen(v => !v)}>{open ? 'Cancel' : 'Log contact'}</button>
      </div>
      {showScript && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--surface-3)', borderRadius: 6, fontSize: 12, lineHeight: 1.55 }}>
          {script}
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>Draft wording. Only if they say they will cancel, you may offer 10% off to keep them.</div>
          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 8px', marginTop: 6 }} onClick={() => navigator.clipboard?.writeText(script)}>Copy</button>
        </div>
      )}
      {open && (
        <div style={{ marginTop: 8, display: 'grid', gap: 8, maxWidth: 420 }}>
          <select className="field-input" value={outcome} onChange={e => setOutcome(e.target.value)}>
            <option value="">Outcome…</option>
            {SWITCH_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <textarea className="field-input" rows={2} placeholder="Note (optional)" value={notes} onChange={e => setNotes(e.target.value)} />
          {err && <div style={{ fontSize: 11, color: '#ef4444' }}>{err}</div>}
          <div><button className="btn" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save contact'}</button></div>
        </div>
      )}
    </div>
  );
}

function MembershipSwitchPanel({ data, clubId, onLogged }) {
  const { user } = useAuth();
  if (!data || data.count === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="section-header">🔁 Membership at the wrong club · {data.count}</div>
      <div className="card" style={{ padding: '6px 20px 12px' }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0 2px' }}>
          Bought at another club but play here 60%+ of the time. Move them to this club's current Nationwide rate; 10% off only to save a cancellation.
        </div>
        {data.tasks.map(t => <SwitchTask key={t.email} t={t} clubId={clubId} repName={user?.name} onLogged={onLogged} />)}
      </div>
    </div>
  );
}

// Guest Verification (10/9/26, Lewisville pilot): guests who verified their phone through the QR form today.
// Staff see the pass status and sponsor result; the guest only ever sees a neutral "verified" screen.
// Spec: docs/COMS_Guest_Verification_Tool_Spec.md. Renders nothing at clubs where the form is not switched on.
const GUEST_PASS = {
  eligible: { label: 'Eligible', bg: 'rgba(34,197,94,0.13)', fg: '#22c55e' },
  used_before: { label: 'Free visit already used', bg: 'rgba(239,68,68,0.13)', fg: '#ef4444' },
  sponsor_issue: { label: 'Sponsor is at another club', bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b' },
  desk_review: { label: 'Needs desk review', bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b' },
  event_guest: { label: 'Event guest', bg: 'rgba(56,189,248,0.13)', fg: '#38bdf8' },
};

const GUEST_PAGE = 'https://www.playpicklr.com/guest';
const ageOn = (birth, on) => {
  const b = new Date(String(birth).slice(0, 10) + 'T12:00:00'); const d = on ? new Date(String(on).slice(0, 10) + 'T12:00:00') : new Date();
  let a = d.getFullYear() - b.getFullYear();
  if (d.getMonth() < b.getMonth() || (d.getMonth() === b.getMonth() && d.getDate() < b.getDate())) a -= 1;
  return a;
};

// Party / event links (10/26/26): a manager creates one link per event; guests who check in through it are
// "Event guest" and do not use up their free guest visit. Parents sign for their children on the same page.
const eventDay = d => new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

function GuestEventLinks({ clubId }) {
  const { get, post } = useApi();
  // Anyone at the club can create a link, front desk included (BK, 10/28/26).
  const canCreate = true;
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');
  const [roster, setRoster] = useState(null);
  const load = useCallback(() => get(`/club-dashboard/guest-events?club=${clubId}`).then(r => setEvents(r.events || [])).catch(() => {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clubId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setErr('');
    try { await post('/club-dashboard/guest-events', { club: clubId, name, eventDate: date }); setName(''); setDate(''); setOpen(false); load(); }
    catch (e) { setErr(e.message || 'Could not create the link.'); }
  };
  const linkFor = code => `${GUEST_PAGE}/${clubId.toLowerCase()}?event=${code}`;
  const copy = code => { navigator.clipboard?.writeText(linkFor(code)); setCopied(code); setTimeout(() => setCopied(''), 2000); };
  if (!events.length && !canCreate) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="section-header">🎉 Upcoming Events · {events.filter(ev => ev.days_away >= 0).length}</div>
      <div className="card" style={{ padding: '8px 20px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Parties and events with their own sign-up link. Families show here as they sign, before the event day.</span>
        {canCreate && !open && <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>+ New event link</button>}
      </div>
      {open && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
          <input className="field-input" style={{ flex: '1 1 200px' }} placeholder='Event name, e.g. "Smith birthday party"' value={name} onChange={e => setName(e.target.value)} />
          <input className="field-input" type="date" style={{ width: 160 }} value={date} onChange={e => setDate(e.target.value)} />
          <button className="btn btn-primary btn-sm" onClick={create} disabled={!name.trim() || !date}>Create link</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setErr(''); }}>Cancel</button>
          {err && <div style={{ width: '100%', fontSize: 12, color: '#ef4444' }}>{err}</div>}
        </div>
      )}
      {[['Today', events.filter(ev => ev.days_away === 0)], ['Upcoming', events.filter(ev => ev.days_away > 0)], ['Recent (last 7 days)', events.filter(ev => ev.days_away < 0)]]
        .filter(([, list]) => list.length).map(([label, list]) => (
          <div key={label} style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)' }}>{label}</div>
            {list.map(ev => (
              <div key={ev.code} style={{ padding: '8px 0', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{ev.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {eventDay(ev.event_date)}{ev.days_away > 0 ? ` · in ${ev.days_away} day${ev.days_away === 1 ? '' : 's'}` : ev.days_away === 0 ? ' · today' : ''}
                  </div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {ev.families
                      ? <><strong>{ev.families}</strong> famil{ev.families === 1 ? 'y' : 'ies'} signed · {ev.adults} adult{ev.adults === 1 ? '' : 's'} playing · {ev.children} child{ev.children === 1 ? '' : 'ren'}
                          {ev.last_signed_at && <span style={{ color: 'var(--text-muted)' }}> · last signed {new Date(ev.last_signed_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>}</>
                      : <span style={{ color: 'var(--text-muted)' }}>No one has signed yet. Send the link to the host.</span>}
                  </div>
                </div>
                <span style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => copy(ev.code)}>{copied === ev.code ? 'Copied' : 'Copy sign-up link'}</button>
                  <button className="btn btn-primary btn-sm" onClick={() => setRoster(ev.code)}>Open roster</button>
                </span>
              </div>
            ))}
          </div>
        ))}
      {!events.length && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '10px 0 2px' }}>No upcoming parties or events.</div>}
      {roster && <GuestEventRoster clubId={clubId} code={roster} link={linkFor(roster)} onClose={() => { setRoster(null); load(); }} />}
      {canCreate && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Send the link to the host to share with every family before the event. Parents sign for their children on the same page, any time before the event; the link stops working the day after it. On the event day, signed guests also appear in Guests Today.</div>}
      </div>
    </div>
  );
}

function formatPhone10(p) {
  const d = String(p || '').replace(/\D/g, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || '');
}

function GuestsTodayPanel({ clubId }) {
  const { get, getBlob } = useApi();
  const openCopy = async id => {
    try { const b = await getBlob(`/club-dashboard/guest-signed-copy?id=${id}`); window.open(URL.createObjectURL(b), '_blank'); }
    catch (e) { alert(e.message); }
  };
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!clubId) return undefined;
    let alive = true;
    const load = () => get(`/club-dashboard/guests?club=${clubId}`).then(r => { if (alive) setData(r); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  if (!data || !data.enabled) return null;
  const guests = data.guests || [];
  return (
    <>
    <GuestEventLinks clubId={clubId} />
    <div style={{ marginBottom: 20 }}>
      <div className="section-header">🎟️ Guests Today · {guests.length}{guests.some(g => (g.children || []).length) ? ` · ${guests.reduce((n, g) => n + (g.children || []).length, 0)} children` : ''}</div>
      <div className="card" style={{ padding: '6px 20px 12px' }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0 2px' }}>
          {data.eventsOnly
            ? 'Party and event guests who signed through an event link, shown on the event day (see Upcoming Events above for sign-ups before then). Children appear under the parent who signed for them.'
            : 'Guests who scanned the QR code and confirmed their phone. One free guest visit per person every 90 days, with a sponsor who is a member of this club. Party and event guests are exempt and appear here on the event day (see Upcoming Events above for sign-ups before then). Children appear under the parent who signed for them.'}
        </div>
        {guests.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>{data.eventsOnly ? 'No event guests today.' : 'No guests have checked in yet today.'}</div>}
        {guests.map(g => {
          const pass = GUEST_PASS[g.pass_status] || GUEST_PASS.desk_review;
          const time = new Date(g.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
          return (
            <div key={g.id} style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{g.full_name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{time} · {g.reference_code}</span>
                </div>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openCopy(g.id)}>Signed copy</button>
                  <span className="badge" style={{ background: pass.bg, color: pass.fg }}>{pass.label}</span>
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {formatPhone10(g.phone10)} · {g.email}
              </div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Sponsor: {g.sponsor_text || 'not given'}{' '}
                <span style={{ color: 'var(--text-muted)' }}>
                  ({g.sponsor_status === 'member_at_club' ? 'member of this club' : g.sponsor_status === 'member_other_club' ? 'member at another club' : g.sponsor_status === 'not_found' ? 'no current member found' : 'name only, please confirm'})
                </span>
                {g.pass_status === 'used_before' && g.prior_visit_date && (
                  <span style={{ color: '#ef4444', marginLeft: 8 }}>
                    Last guest visit {String(g.prior_visit_date).slice(0, 10)}{g.prior_club ? ` at ${g.prior_club}` : ''}
                  </span>
                )}
                {g.marketing_opt_in && <span className="badge" style={{ marginLeft: 8, background: 'rgba(56,189,248,0.13)', color: '#38bdf8' }}>OK to text offers</span>}
                {(g.interests || []).includes('junior_programs') && <span className="badge" style={{ marginLeft: 8, background: 'rgba(168,85,247,0.13)', color: '#a855f7' }}>Wants junior program info</span>}
                {g.home_zip && (g.in_market === false
                  ? <span className="badge" style={{ marginLeft: 8, background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>Out of area · {g.home_city ? `${g.home_city} ` : ''}{g.home_zip}</span>
                  : <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{g.home_city ? `${g.home_city} ` : ''}{g.home_zip}</span>)}
                {g.event_name && <span className="badge" style={{ marginLeft: 8, background: 'rgba(56,189,248,0.13)', color: '#38bdf8' }}>🎉 {g.event_name}</span>}
                {g.checkin_role === 'children' && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>(parent not playing)</span>}
              </div>
              {(g.children || []).map(k => (
                <div key={k.id} style={{ fontSize: 12, marginTop: 4, paddingLeft: 14, borderLeft: '2px solid var(--border)' }}>
                  <strong>{k.firstName} {k.lastName}</strong>
                  <span style={{ color: 'var(--text-muted)' }}> · age {ageOn(k.birthDate, g.visit_date)}</span>
                  <span className="badge" style={{ marginLeft: 8, background: 'rgba(34,197,94,0.13)', color: '#22c55e' }}>Parent consent until {String(k.consentUntil).slice(0, 10)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
    </>
  );
}

// Referral Program Phase 1 — card issuance lookup strip. WI pilot only
// (GB/MF/WA). See docs/referral/COMS_Referral_Program_Build_Spec.md.
// Deliberately a lookup-driven strip, not a flag on every In Club Today row —
// most eligible members won't ask about it on a given day, so surfacing it
// as a flag for all of them would be noise (see build spec Section 4.3).
const REFERRAL_WI_CLUBS = ['GB', 'MF', 'WA'];

const INELIGIBLE_REASON_LABELS = {
  not_valid: 'Not an active member',
  different_home_club: 'Home club is a different location',
  trial: 'Trial member',
  welld: 'Welld / insurance plan',
};

function ReferralLookupStrip({ clubId }) {
  const { get, post } = useApi();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [issuingEmail, setIssuingEmail] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError(null);
      get(`/referral/lookup?q=${encodeURIComponent(query.trim())}&locationId=${clubId}`)
        .then(d => setResults(d.results))
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, clubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const issueCards = (email) => {
    setIssuingEmail(email);
    post('/referral/issue-cards', { email, locationId: clubId })
      .then(() => {
        setResults(rs => rs.map(r => r.email === email
          ? { ...r, eligible: false, alreadyIssued: true, issuedAt: new Date().toISOString() }
          : r));
      })
      .catch(e => {
        // A 409 here almost always means someone else issued it a moment
        // ago — refresh that row's state rather than showing a raw error.
        setResults(rs => rs.map(r => r.email === email
          ? { ...r, eligible: false, alreadyIssued: true }
          : r));
        if (!e.message.includes('409')) setError(e.message);
      })
      .finally(() => setIssuingEmail(null));
  };

  if (!REFERRAL_WI_CLUBS.includes(clubId)) return null;

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: results ? 10 : 0 }}>
        <span style={{ fontSize: 16 }}>🎟️</span>
        <input
          type="text"
          placeholder="Referral Cards — search member by name, email, or phone…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{
            flex: 1, background: 'var(--surface-2)', border: '1px solid var(--border)',
            color: 'var(--text)', padding: '6px 10px', borderRadius: 6, fontSize: 13,
          }}
        />
        {loading && <div className="spinner" style={{ width: 16, height: 16 }} />}
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>{error}</div>}

      {results && results.length === 0 && !loading && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No matches.</div>
      )}

      {results && results.length > 0 && (
        <div>
          {results.map(r => (
            <div
              key={r.email}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 0', borderTop: '1px solid var(--border)', gap: 10, flexWrap: 'wrap',
              }}
            >
              <div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{r.name || r.email}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
                  {r.phone || r.email}
                </span>
              </div>
              {r.alreadyIssued ? (
                <span className="badge" style={{ background: 'rgba(34,197,94,0.13)', color: '#22c55e' }}>
                  Issued{r.issuedAt ? ` ${new Date(r.issuedAt).toLocaleDateString()}` : ''}
                </span>
              ) : r.eligible ? (
                <button
                  onClick={() => issueCards(r.email)}
                  disabled={issuingEmail === r.email}
                  style={{
                    background: 'var(--accent, #38bdf8)', border: 'none', color: '#fff',
                    padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    cursor: issuingEmail === r.email ? 'default' : 'pointer',
                    opacity: issuingEmail === r.email ? 0.6 : 1,
                  }}
                >
                  {issuingEmail === r.email ? 'Issuing…' : 'Issue 5 Referral Cards'}
                </button>
              ) : (
                <span className="badge" style={{ background: 'rgba(255,255,255,0.08)', color: '#999' }}>
                  {INELIGIBLE_REASON_LABELS[r.ineligibleReason] || 'Not eligible'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Referral Program Phase 3 — credit reconciliation. BK, 9/24/26: deliberately
// NOT automated — a club reviews this monthly and FD only gets the script
// once THEY confirm both real-world facts (converted, stayed a full month).
// Collapsed by default: this is a month-end task, not something that should
// compete for attention with the daily lookup strip above it.
function ReferralReconciliationPanel({ clubId }) {
  const { get, post } = useApi();
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [scripts, setScripts] = useState({}); // id -> script text, once confirmed
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!expanded || rows !== null) return;
    setLoading(true);
    setError(null);
    get(`/referral/reconciliation?locationId=${clubId}`)
      .then(d => setRows(d.referrals))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [expanded, clubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmCredit = (id) => {
    setConfirmingId(id);
    post(`/referral/reconciliation/${id}/credit`, {})
      .then(d => {
        setScripts(s => ({ ...s, [id]: d.script }));
        setRows(rs => rs.map(r => r.id === id
          ? { ...r, credit_status: 'paid', credit_amount: d.credit_amount, referrer_credit_paid_total: d.referrer_credit_paid_total }
          : r));
      })
      .catch(e => setError(e.message))
      .finally(() => setConfirmingId(null));
  };

  const copyScript = (id, text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!REFERRAL_WI_CLUBS.includes(clubId)) return null;

  const pendingCount = (rows || []).filter(r => r.credit_status !== 'paid').length;

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 20 }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
      >
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          🎟️ Referral Credit Reconciliation{rows ? ` (${pendingCount} pending)` : ''}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{expanded ? '▲ Hide' : '▼ Review month-end list'}</span>
      </div>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {loading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
          {error && <div style={{ color: 'var(--red)', fontSize: 12 }}>{error}</div>}
          {rows && rows.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No referrals yet for this club.</div>
          )}
          {rows && rows.map(r => {
            const script = scripts[r.id];
            return (
              <div key={r.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{r.referrer_name_submitted}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
                      referred {r.guest_name}
                    </span>
                  </div>
                  {r.credit_status === 'paid' ? (
                    <span className="badge" style={{ background: 'rgba(34,197,94,0.13)', color: '#22c55e' }}>
                      ✓ Credited ${r.credit_amount}
                    </span>
                  ) : !r.converted ? (
                    <span className="badge" style={{ background: 'rgba(255,255,255,0.08)', color: '#999' }}>Not yet converted</span>
                  ) : !r.month_elapsed ? (
                    <span className="badge" style={{ background: 'rgba(250,204,21,0.13)', color: '#facc15' }}>Converted — under 30 days</span>
                  ) : !r.stayed_full_month ? (
                    <span className="badge" style={{ background: 'rgba(239,68,68,0.13)', color: '#ef4444' }}>Left before 30 days</span>
                  ) : (
                    <button
                      onClick={() => confirmCredit(r.id)}
                      disabled={confirmingId === r.id}
                      style={{
                        background: 'var(--accent, #38bdf8)', border: 'none', color: '#fff',
                        padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        cursor: confirmingId === r.id ? 'default' : 'pointer',
                        opacity: confirmingId === r.id ? 0.6 : 1,
                      }}
                    >
                      {confirmingId === r.id ? 'Confirming…' : 'Confirm converted & stayed a month → Get script'}
                    </button>
                  )}
                </div>
                {script && (
                  <div style={{ marginTop: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5, marginBottom: 8 }}>{script}</div>
                    <button
                      onClick={() => copyScript(r.id, script)}
                      style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                    >
                      {copiedId === r.id ? '✓ Copied' : '📋 Copy script'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Totals page (BK, 10/23/26) ──────────────────────────────────────────────
// Same idea as the Sales Dashboard: one card per area the club owns, with an active count, people contacted
// against a goal, and one result number. Goals are standing per club and set by an admin only. The old panels
// (In Club Today, Trials Needing Help, Trial Goal) are gone from this page until retention is defined and they
// can be shown as numbers; the lookup tools and the wrong-club list below stay.
const GRANULARITIES = [
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];

const AREAS = [
  { key: 'pb101', icon: '🏓', route: '/pb101-pipeline', detail: d => [
    ['Registered', d.registered], ['Trial ask', d.dayOfTrialAsk], ['Next step', d.nextStep], ['No-show', d.noShow], ['Follow-up', d.followUpClub]] },
  { key: 't2m', icon: '🌱', route: '/t2m-pipeline', detail: d => [
    ['On a trial', d.onTrial], ['Membership asked', d.membershipAsked]] },
  { key: 'retention', icon: '🔄', route: '/retention-pipeline', detail: d => [
    ['At risk', d.atRisk], ['Welld', d.welld], ['Payment failed', d.paymentFailed], ['Cancelling', d.cancelling]] },
];

const GOAL_ROWS = [
  { metric: 'result_t2m', label: 'Trials: converted to a membership', periods: ['week', 'month'] },
  { metric: 'result_retention', label: 'Retention: saved', periods: ['week', 'month'] },
];

// PB101 goals are rates: one standing target percent each, shown against the real n/d.
const RATE_ROWS = [
  { metric: 'rate_pb101_fill', label: 'Sessions with 4+ people signed up' },
  { metric: 'rate_pb101_ask', label: 'Attendees asked about a trial at the club' },
];

// Follow-ups have their own card and targets.
const TRIAL_RATE_ROWS = [
  { metric: 'rate_t2m_manager', label: 'Trials with a manager connection (past halfway)' },
];

const FOLLOWUP_RATE_ROWS = [
  { metric: 'rate_followup_ontime', label: 'Scheduled follow-ups not overdue' },
  { metric: 'rate_followup_contacted', label: 'Waiting PB101 follow-ups contacted' },
];

function rateColor(num, den, target) {
  if (!den) return '#ccc';
  const pct = (num / den) * 100;
  if (target == null) return '#ccc';
  if (pct >= target) return '#22c55e';
  if (pct >= target * 0.8) return '#f59e0b';
  return '#ef4444';
}
const pctText = (num, den) => (den ? `${Math.round((num / den) * 1000) / 10}%` : '—');

function RateRow({ label, rate, target, note }) {
  return (
    <div style={{ ...statBoxStyle(), marginBottom: 6 }} onClick={e => e.stopPropagation()}>
      <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: rateColor(rate.num, rate.den, target) }}>{pctText(rate.num, rate.den)}</div>
        <div style={{ fontSize: 11, color: '#aaa' }}>{rate.num}/{rate.den}</div>
        <div style={{ fontSize: 10, color: '#666', marginLeft: 'auto' }}>{target != null ? `target ${target}%` : 'no target set'}</div>
      </div>
      {note && <div style={{ fontSize: 9, color: '#666' }}>{note}</div>}
    </div>
  );
}

function statBoxStyle() {
  return { background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7, padding: '6px 10px' };
}

function paceColor(pacePct) {
  if (pacePct === null || pacePct === undefined) return '#ccc';
  if (pacePct >= 100) return '#22c55e';
  if (pacePct >= 70) return '#f59e0b';
  return '#ef4444';
}

function AreaCard({ area, card, clubNameById, clubs, onNavigate }) {
  const [gran, setGran] = useState('week');
  const [byClubOpen, setByClubOpen] = useState(false);
  const p = card.periods[gran];
  const resGran = gran === 'day' ? 'week' : gran;
  const r = card.results[resGran];
  const granLabel = GRANULARITIES.find(g => g.key === gran).label;
  const resLabel = GRANULARITIES.find(g => g.key === resGran).label;

  // Totals detail across the selected clubs.
  const sumDetail = {};
  for (const c of clubs) for (const [k, v] of Object.entries(card.byClub[c].detail)) sumDetail[k] = (sumDetail[k] || 0) + v;
  const lines = area.detail(sumDetail);

  return (
    <div className="card" style={{ cursor: 'pointer', minWidth: 280 }} onClick={() => onNavigate(area.route)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{area.icon} {card.label}</div>
        <div style={{ fontSize: 11, color: '#888' }}>{card.activeCount} active</div>
      </div>
      <div style={{ fontSize: 10.5, color: '#888', marginBottom: 8, lineHeight: 1.5 }}>
        {lines.map(([l, n]) => `${l} ${n || 0}`).join(' · ')}
      </div>
      {area.key === 't2m' && sumDetail.tasks > 0 && (
        <div style={{ fontSize: 10.5, color: '#f59e0b', marginBottom: 8 }}>
          {sumDetail.tasks} trial{sumDetail.tasks === 1 ? '' : 's'} need help now
          {' '}({sumDetail.tasksEnding} ending · {sumDetail.tasksQuiet} quiet · {sumDetail.tasksBehind} behind)
        </div>
      )}
      {area.key === 't2m' && sumDetail.managerDue > 0 && (
        <div style={{ fontSize: 10.5, color: '#a78bfa', marginBottom: 8 }}>
          {sumDetail.managerDue} need a manager connection
        </div>
      )}
      {area.key === 'retention' && (sumDetail.overdue > 0 || sumDetail.notContacted > 0) && (
        <div style={{ fontSize: 10.5, color: sumDetail.overdue > 0 ? '#ef4444' : '#f59e0b', marginBottom: 8 }}>
          {sumDetail.overdue > 0 ? `${sumDetail.overdue} past deadline · ` : ''}{sumDetail.notContacted} not contacted yet
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }} onClick={e => e.stopPropagation()}>
        {GRANULARITIES.map(g => (
          <button key={g.key} onClick={() => setGran(g.key)} style={{
            fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${gran === g.key ? '#3b82f6' : '#333'}`,
            background: gran === g.key ? '#3b82f615' : '#111', color: gran === g.key ? '#3b82f6' : '#777',
          }}>{g.label}</button>
        ))}
      </div>

      {area.key === 'pb101' && card.rates ? (
        <>
          <RateRow label={`Sessions with 4+ people — ${resLabel}`} rate={card.rates.totals.fill[resGran]} target={card.rates.totals.targets.fill}
            note="Signed up and not marked absent (attendance is not always recorded)" />
          <RateRow label={`Asked about a trial at the club — ${resLabel}`} rate={card.rates.totals.ask[resGran]} target={card.rates.totals.targets.ask}
            note="Counts a logged ask, or a trial started within 2 days" />
          <div style={{ ...statBoxStyle(), marginBottom: 6 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{card.resultLabel} — {resLabel}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#ccc' }}>{r.count}</div>
          </div>
        </>
      ) : (
      <>
      {card.rates?.totals?.manager && (
        <RateRow label="Manager connection by halfway — now" rate={card.rates.totals.manager} target={card.rates.totals.targets.manager}
          note="Active trials past their midpoint with a manager connection logged" />
      )}
      <div style={statBoxStyle()} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{card.resultLabel} — {resLabel}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: paceColor(r.pacePct) }}>
          {r.count}{r.goalAmount != null ? ` / ${r.goalAmount}` : ''}
        </div>
        <div style={{ fontSize: 9, color: '#666' }}>{r.pacePct !== null ? `${r.pacePct}% of goal` : 'No goal set'}</div>
      </div>
      </>
      )}

      <div style={{ ...statBoxStyle(), marginTop: 6 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>People contacted — {granLabel}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#ccc' }}>{p.people}</div>
        <div style={{ fontSize: 9, color: '#666' }}>Activity, no goal</div>

        {clubs.length > 1 && (
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 9.5, marginTop: 6, padding: '2px 6px' }}
            onClick={e => { e.stopPropagation(); setByClubOpen(o => !o); }}>
            {byClubOpen ? '▾ Hide by club' : '▸ Show by club'}
          </button>
        )}
        {byClubOpen && (
          area.key === 'pb101' && card.rates ? (
          <table style={{ width: '100%', fontSize: 10.5, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#888', textAlign: 'right' }}>
              <th style={{ textAlign: 'left', fontWeight: 500 }}>Club</th><th style={{ fontWeight: 500 }}>4+ people</th>
              <th style={{ fontWeight: 500 }}>Asked</th>
            </tr></thead>
            <tbody>
              {clubs.map(c => {
                const v = card.rates.byClub[c];
                const cell = (rate, target) => <td style={{ color: rateColor(rate.num, rate.den, target) }}>{rate.num}/{rate.den} ({pctText(rate.num, rate.den)})</td>;
                return (
                  <tr key={c} style={{ textAlign: 'right' }}>
                    <td style={{ textAlign: 'left' }}>{clubNameById[c] || c}</td>
                    {cell(v.fill[resGran], v.targets.fill)}{cell(v.ask[resGran], v.targets.ask)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          ) : (
          <table style={{ width: '100%', fontSize: 10.5, marginTop: 6, borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#888', textAlign: 'right' }}>
              <th style={{ textAlign: 'left', fontWeight: 500 }}>Club</th><th style={{ fontWeight: 500 }}>Active</th>
              <th style={{ fontWeight: 500 }}>Result</th><th style={{ fontWeight: 500 }}>Contacted</th>
            </tr></thead>
            <tbody>
              {clubs.map(c => {
                const b = card.byClub[c];
                return (
                  <tr key={c} style={{ textAlign: 'right' }}>
                    <td style={{ textAlign: 'left' }}>{clubNameById[c] || c}</td>
                    <td>{b.activeCount}</td>
                    <td style={{ color: paceColor(b.results[resGran].pacePct) }}>{b.results[resGran].count}{b.results[resGran].goalAmount != null ? `/${b.results[resGran].goalAmount}` : ''}</td>
                    <td>{b.periods[gran].people}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )
        )}
      </div>
    </div>
  );
}

function FollowUpsCard({ fu, clubs, clubNameById, onNavigate }) {
  const [byClubOpen, setByClubOpen] = useState(false);
  const BOARDS = [['pb101', 'PB101', '/pb101-pipeline'], ['t2m', 'Trials', '/t2m-pipeline'], ['retention', 'Retention', '/retention-pipeline']];
  return (
    <div className="card" style={{ minWidth: 280 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>📅 Follow-ups</div>
        <div style={{ fontSize: 11, color: '#888' }}>{fu.open} scheduled</div>
      </div>
      <div style={{ fontSize: 10.5, color: fu.overdue > 0 ? '#ef4444' : '#888', marginBottom: 8 }}>
        {fu.overdue > 0 ? `${fu.overdue} overdue` : 'None overdue'}
      </div>
      <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse', marginBottom: 8 }}>
        <thead><tr style={{ color: '#888', textAlign: 'right' }}>
          <th style={{ textAlign: 'left', fontWeight: 500 }}>Board</th><th style={{ fontWeight: 500 }}>Overdue</th>
          <th style={{ fontWeight: 500 }}>Today</th><th style={{ fontWeight: 500 }}>Upcoming</th>
        </tr></thead>
        <tbody>
          {BOARDS.map(([k, label, route]) => (
            <tr key={k} style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => onNavigate(route)}>
              <td style={{ textAlign: 'left' }}>{label}</td>
              <td style={{ color: fu.boards[k].overdue > 0 ? '#ef4444' : '#ccc' }}>{fu.boards[k].overdue}</td>
              <td>{fu.boards[k].today}</td><td>{fu.boards[k].upcoming}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <RateRow label="Scheduled follow-ups not overdue" rate={fu.ontime} target={fu.targets.ontime} note="Scheduled by club staff, still open" />
      <RateRow label="Waiting PB101 follow-ups contacted" rate={fu.contacted} target={fu.targets.contacted}
        note="Post-session, no-show and win-back people with a logged contact since their session" />
      {clubs.length > 1 && (
        <>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 9.5, padding: '2px 6px' }} onClick={() => setByClubOpen(o => !o)}>
            {byClubOpen ? '▾ Hide by club' : '▸ Show by club'}
          </button>
          {byClubOpen && (
            <table style={{ width: '100%', fontSize: 10.5, marginTop: 6, borderCollapse: 'collapse' }}>
              <thead><tr style={{ color: '#888', textAlign: 'right' }}>
                <th style={{ textAlign: 'left', fontWeight: 500 }}>Club</th><th style={{ fontWeight: 500 }}>Not overdue</th><th style={{ fontWeight: 500 }}>Contacted</th>
              </tr></thead>
              <tbody>
                {clubs.map(c => {
                  const v = fu.byClub[c];
                  return (
                    <tr key={c} style={{ textAlign: 'right' }}>
                      <td style={{ textAlign: 'left' }}>{clubNameById[c] || c}</td>
                      <td style={{ color: rateColor(v.ontime.num, v.ontime.den, v.targets.ontime) }}>{v.ontime.num}/{v.ontime.den} ({pctText(v.ontime.num, v.ontime.den)})</td>
                      <td style={{ color: rateColor(v.contacted.num, v.contacted.den, v.targets.contacted) }}>{v.contacted.num}/{v.contacted.den} ({pctText(v.contacted.num, v.contacted.den)})</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function FreeCard({ free, clubs, clubNameById }) {
  const over = free.cap != null && free.neverPaid > free.cap;
  return (
    <div className="card" style={{ minWidth: 280 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>🎟️ Free memberships</div>
        <div style={{ fontSize: 11, color: '#888' }}>$0 lifetime payments</div>
      </div>
      <div style={statBoxStyle()}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>Against the cap</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: free.cap == null ? '#ccc' : over ? '#ef4444' : '#22c55e' }}>
          {free.neverPaid}{free.cap != null ? ` / ${free.cap}` : ''}
        </div>
        <div style={{ fontSize: 9, color: '#666' }}>{free.cap == null ? 'No cap set' : over ? `${free.neverPaid - free.cap} over the cap` : 'Within the cap'}</div>
      </div>
      {clubs.length > 1 && (
        <table style={{ width: '100%', fontSize: 10.5, marginTop: 8, borderCollapse: 'collapse' }}>
          <tbody>
            {clubs.map(c => {
              const b = free.byClub[c];
              const o = b && b.cap != null && b.neverPaid > b.cap;
              return (
                <tr key={c} style={{ textAlign: 'right' }}>
                  <td style={{ textAlign: 'left' }}>{clubNameById[c] || c}</td>
                  <td style={{ color: o ? '#ef4444' : '#ccc' }}>{b ? `${b.neverPaid}${b.cap != null ? ` / ${b.cap}` : ''}` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Admin-only: standing goals per club (day / week / month). A blank box clears the goal.
function GoalsPanel({ overview, clubNameById, onSaved }) {
  const { post } = useApi();
  const [open, setOpen] = useState(false);
  const [club, setClub] = useState(overview.clubs[0]);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const current = overview.goals[club] || {};
  const keyOf = (m, p) => `${m}|${p}`;
  const shown = (m, p) => {
    const k = keyOf(m, p);
    return k in vals ? vals[k] : (current[m]?.[p]?.amount ?? '');
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      for (const [k, v] of Object.entries(vals)) {
        const [metric, period] = k.split('|');
        await post('/club-dashboard/goal', { locationId: club, metric, period, goalAmount: v === '' ? null : Number(v) });
      }
      setVals({});
      setMsg('Saved');
      onSaved();
    } catch (e) {
      setMsg(e.message || 'Could not save');
    } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ padding: '10px 14px', marginBottom: 12 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>🎯 Club goals (admin)</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{open ? '▲ Hide' : '▼ Set goals'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <select value={club} onChange={e => { setClub(e.target.value); setVals({}); setMsg(''); }}
              style={{ background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '5px 8px', fontSize: 12 }}>
              {overview.clubs.map(c => <option key={c} value={c}>{clubNameById[c] || c}</option>)}
            </select>
            <span style={{ fontSize: 11, color: '#888' }}>Standing goals: they repeat until changed. Trials and Retention take outcome counts; PB101 takes target percents. Contacts are shown as activity, with no goal.</span>
          </div>
          <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#888' }}>
              <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 16 }}>Goal</th>
              {GRANULARITIES.map(g => <th key={g.key} style={{ fontWeight: 500, padding: '0 6px' }}>{g.label}</th>)}
            </tr></thead>
            <tbody>
              {GOAL_ROWS.map(row => (
                <tr key={row.metric}>
                  <td style={{ paddingRight: 16, paddingTop: 4 }}>{row.label}</td>
                  {GRANULARITIES.map(g => (
                    <td key={g.key} style={{ padding: '2px 6px' }}>
                      {row.periods.includes(g.key) ? (
                        <input type="number" min="0" value={shown(row.metric, g.key)}
                          onChange={e => setVals(v => ({ ...v, [keyOf(row.metric, g.key)]: e.target.value }))}
                          style={{ width: 64, background: '#111', border: '1px solid #333', color: '#ddd', borderRadius: 4, padding: '3px 6px', fontSize: 12 }} />
                      ) : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <table style={{ fontSize: 12, borderCollapse: 'collapse', marginTop: 12 }}>
            <thead><tr style={{ color: '#888' }}>
              <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 16 }}>PB101 target (percent)</th>
              <th style={{ fontWeight: 500, padding: '0 6px' }}>Target %</th>
            </tr></thead>
            <tbody>
              {RATE_ROWS.map(row => (
                <tr key={row.metric}>
                  <td style={{ paddingRight: 16, paddingTop: 4 }}>{row.label}</td>
                  <td style={{ padding: '2px 6px' }}>
                    <input type="number" min="0" max="100" value={shown(row.metric, 'month')}
                      onChange={e => setVals(v => ({ ...v, [keyOf(row.metric, 'month')]: e.target.value }))}
                      style={{ width: 64, background: '#111', border: '1px solid #333', color: '#ddd', borderRadius: 4, padding: '3px 6px', fontSize: 12 }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <table style={{ fontSize: 12, borderCollapse: 'collapse', marginTop: 12 }}>
            <thead><tr style={{ color: '#888' }}>
              <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 16 }}>Trials target (percent)</th>
              <th style={{ fontWeight: 500, padding: '0 6px' }}>Target %</th>
            </tr></thead>
            <tbody>
              {TRIAL_RATE_ROWS.map(row => (
                <tr key={row.metric}>
                  <td style={{ paddingRight: 16, paddingTop: 4 }}>{row.label}</td>
                  <td style={{ padding: '2px 6px' }}>
                    <input type="number" min="0" max="100" value={shown(row.metric, 'month')}
                      onChange={e => setVals(v => ({ ...v, [keyOf(row.metric, 'month')]: e.target.value }))}
                      style={{ width: 64, background: '#111', border: '1px solid #333', color: '#ddd', borderRadius: 4, padding: '3px 6px', fontSize: 12 }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <table style={{ fontSize: 12, borderCollapse: 'collapse', marginTop: 12 }}>
            <thead><tr style={{ color: '#888' }}>
              <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 16 }}>Follow-up target (percent)</th>
              <th style={{ fontWeight: 500, padding: '0 6px' }}>Target %</th>
            </tr></thead>
            <tbody>
              {FOLLOWUP_RATE_ROWS.map(row => (
                <tr key={row.metric}>
                  <td style={{ paddingRight: 16, paddingTop: 4 }}>{row.label}</td>
                  <td style={{ padding: '2px 6px' }}>
                    <input type="number" min="0" max="100" value={shown(row.metric, 'month')}
                      onChange={e => setVals(v => ({ ...v, [keyOf(row.metric, 'month')]: e.target.value }))}
                      style={{ width: 64, background: '#111', border: '1px solid #333', color: '#ddd', borderRadius: 4, padding: '3px 6px', fontSize: 12 }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={save} disabled={saving || Object.keys(vals).length === 0}
              style={{ background: 'var(--accent, #38bdf8)', border: 'none', color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving || !Object.keys(vals).length ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Save goals'}
            </button>
            {msg && <span style={{ fontSize: 12, color: msg === 'Saved' ? '#22c55e' : '#ef4444' }}>{msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClubDashboardModule() {
  const { get } = useApi();
  const { currentClub, availableClubs } = useClub();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const clubId = currentClub?.location_id || currentClub?.id;
  const [allClubs, setAllClubs] = useState(false);
  const [overview, setOverview] = useState(null);
  const [switchData, setSwitchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTrend, setShowTrend] = useState(false);

  const canAll = availableClubs.length > 1;
  const clubNameById = Object.fromEntries(availableClubs.map(c => [c.id, c.name]));

  const load = useCallback(() => {
    if (!clubId) return;
    setLoading(true);
    setError(null);
    const clubs = allClubs ? availableClubs.map(c => c.id).filter(c => c !== 'DEN').join(',') : clubId;
    get(`/club-dashboard/overview?clubs=${clubs}`)
      .then(setOverview)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    get(`/club-dashboard/summary?locationId=${clubId}&parts=switch`).then(setSwitchData).catch(() => setSwitchData(null));
  }, [clubId, allClubs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return (
    <div className="module-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>📊</span>
          <h1 className="module-title">Club Dashboard</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canAll && (
            <button onClick={() => setAllClubs(v => !v)}
              style={{ background: allClubs ? 'var(--surface-2)' : 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
              {allClubs ? 'All clubs ✓' : 'All clubs'}
            </button>
          )}
          <button onClick={() => setShowTrend(v => !v)} aria-expanded={showTrend}
            style={{ background: showTrend ? 'var(--surface-2)' : 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            📉 Retention trend {showTrend ? '▴' : '▾'}
          </button>
        </div>
      </div>

      {showTrend && (
        <div style={{ marginBottom: 20 }}>
          <RetentionTrendPanel clubId={clubId} clubName={currentClub?.name} isAdmin={isAdmin} />
        </div>
      )}

      {loading && !overview && <div className="empty-state"><div className="spinner" /></div>}
      {error && (
        <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
          Failed to load — {error}
          <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {overview && !error && (
        <>
          {isAdmin && <GoalsPanel key={overview.clubs.join(',')} overview={overview} clubNameById={clubNameById} onSaved={load} />}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 20, opacity: loading ? 0.6 : 1 }}>
            {AREAS.map(a => (
              <AreaCard key={a.key} area={a} card={overview.cards[a.key]} clubs={overview.clubs} clubNameById={clubNameById} onNavigate={navigate} />
            ))}
            <FollowUpsCard fu={overview.followups} clubs={overview.clubs} clubNameById={clubNameById} onNavigate={navigate} />
            <FreeCard free={overview.free} clubs={overview.clubs} clubNameById={clubNameById} />
          </div>

          <ReferralLookupStrip clubId={clubId} />
          <ReferralReconciliationPanel clubId={clubId} />
          <GuestsTodayPanel clubId={clubId} />
          {switchData?.membershipSwitch && <MembershipSwitchPanel data={switchData.membershipSwitch} clubId={clubId} onLogged={load} />}
        </>
      )}
    </div>
  );
}
